/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SystemModule
 */

import {
    copy,
    IHttpResponse,
    IPromise,
} from 'angular';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';

import {
    IBackupConfiguration,
    IScheduler,
} from 'generated-types';

import { L10nService } from '@vmw/ngx-vip';
import { SchedulerModalComponent } from 'ng/modules/system';
import { withFullModalMixin } from 'ajs/js/utilities/mixins';
import * as l10n from '../system.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

export interface IExtendedSchedulerConfig extends IScheduler {
    backup_config_ref_data: IBackupConfiguration;
}

interface ISchedulerMacroConfig {
    model_name: string;
    data: IExtendedSchedulerConfig;
}

/**
 * @description Scheduler Item class for setting up Backup Scheduler.
 * @author Ratan Kumar
 */
export class Scheduler extends withFullModalMixin(ObjectTypeItem) {
    public static ajsDependencies = [
        'l10nService',
    ];

    public getConfig: () => IExtendedSchedulerConfig;

    private readonly l10nService: L10nService;

    constructor(args: {}) {
        const extendedArgs = {
            objectName: 'scheduler',
            objectType: 'Scheduler',
            windowElement: SchedulerModalComponent,
            params: {
                include_name: true,
                join: 'backupconfiguration:backup_config_ref',
            },
            ...args,
        };

        super(extendedArgs);

        this.l10nService = this.getAjsDependency_('l10nService');

        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public dataToSave(): ISchedulerMacroConfig {
        const config = copy(this.getConfig());

        return {
            model_name: 'scheduler',
            data: config,
        };
    }

    /**
     * @override
     */
    public urlToSave(): string {
        return '/api/macro';
    }

    /**
     * @override
     * Reloads the item upon saving new settings
     */
    protected saveRequest(): IPromise<IHttpResponse<IExtendedSchedulerConfig>> {
        return super.saveRequest()
            .then(() => this.load());
    }

    /**
     * Getter for backup_passphrase
     */
    public get backupPassphrase(): string {
        return this.backupConfigRefData?.backup_passphrase;
    }

    /**
     * Setter for backup_passphrase
     */
    public set backupPassphrase(passphrase: string) {
        this.backupConfigRefData.backup_passphrase = passphrase;
    }

    /**
     * Getter for backup_config_ref_data
     */
    public get backupConfigRefData(): IBackupConfiguration {
        return this.getConfig().backup_config_ref_data;
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(l10nKeys.schedulerModalBreadcrumbTitle);
    }
}
